import React from "react";
import { cn } from "@/lib/utils";
import { Check, LucideIcon } from "lucide-react";
import { useTranslation } from "@/context/LanguageContext";

export const PricingCard = ({
  tier,
  planKey,
  price,
  features,
  buttonText,
  highlight,
  badge,
  billingCycle,
  isTeam,
  buttonIcon,
  onButtonClick,
}: {
  tier: string;
  planKey: "free" | "pro" | "team";
  price: number;
  badge?: string;
  features: (
    | string
    | {
        text: string;
        bold?: boolean;
        boldWords?: string[];
      }
  )[];
  buttonText: string;
  highlight?: boolean;
  billingCycle: "monthly" | "yearly";
  isTeam?: boolean;
  buttonIcon?: LucideIcon;
  onButtonClick?: () => void;
}) => {
  const { t, formatNumber } = useTranslation();

  const renderFeatureText = (
    feature:
      | string
      | {
          text: string;
          bold?: boolean;
          boldWords?: string[];
        }
  ) => {
    if (typeof feature === "string") {
      return <span>{feature}</span>;
    }
    if (feature.bold) {
      return <span className="font-semibold">{feature.text}</span>;
    }
    if (feature.boldWords && feature.boldWords.length > 0) {
      const parts = feature.text.split(
        new RegExp(`(${feature.boldWords.join("|")})`, "gi")
      );
      return (
        <span>
          {parts.map((part, idx) =>
            feature.boldWords?.some(
              (word) => word.toLowerCase() === part.toLowerCase()
            ) ? (
              <span key={idx} className="font-semibold">
                {part}
              </span>
            ) : (
              <span key={idx}>{part}</span>
            )
          )}
        </span>
      );
    }
    return <span>{feature.text}</span>;
  };

  const descMap = { free: "pricing.freeDesc", pro: "pricing.proDesc", team: "pricing.teamDesc" } as const;
  const tierDesc = t(descMap[planKey]);

  return (
    <div
      className={cn(
        "flex flex-col border-2 rounded-3xl p-8 transition-all duration-300 hover:shadow-lg",
        highlight
          ? "bg-primary text-primary-foreground border-primary/20 shadow-xl scale-105"
          : "bg-card border-border"
      )}
    >
      {/* Top header */}
      <div className="mb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-4xl font-light">
              <span className="font-medium text-2xl">{tier}</span>
            </h3>
            {(isTeam || highlight) && (
              <span className="text-xs opacity-70">
                {billingCycle === "monthly" ? t("pricing.billedMonthly") : t("pricing.billedYearly")}
              </span>
            )}
          </div>
          {badge && (
            <span
              className={cn(
                "text-xs px-3 py-1 rounded-full font-semibold shadow-sm",
                highlight
                  ? "bg-primary-foreground text-primary"
                  : "bg-primary text-primary-foreground"
              )}
            >
              {badge}
            </span>
          )}
        </div>

        {/* Pricing */}
        <div className="mt-4">
          <div className="flex items-end justify-start w-full">
            <div>
              <span className="text-5xl">
                <span className="font-light">SR</span>
                <span className="ml-1.5">{planKey === "free" ? formatNumber(0) : formatNumber(price)}</span>
              </span>
              <span className="text-lg font-light opacity-70">
                {isTeam ? ` ${t("pricing.perMonthPerSeat")}` : ` ${t("pricing.perMonth")}`}
              </span>
              {isTeam && (
                <div className="text-xs opacity-60 mt-1.5">
                  {t("pricing.minSeats")}
                </div>
              )}
            </div>
          </div>
        </div>

        <p className="text-base mt-4">{tierDesc}</p>

        <div className="bg-border w-full h-[1px] mt-3 mb-1" />
      </div>

      {/* Features */}
      <ul className="flex flex-col gap-2 text-sm mb-6 flex-grow min-h-[15rem]">
        {features.map((f, i) => (
          <li key={i} className="flex items-start w-full">
            <Check
              className={cn(
                "w-4 h-4 mt-1 mr-4 flex-shrink-0",
                highlight ? "text-primary-foreground" : "text-correct"
              )}
            />
            <div className="flex-grow flex items-start text-sm">
              <span className="mb-2">{renderFeatureText(f)}</span>
            </div>
          </li>
        ))}
      </ul>

      {/* CTA */}
      <button
        onClick={() => onButtonClick ? onButtonClick() : console.log(`CTA for ${tier}`)}
        className={cn(
          "w-full h-12 flex items-center justify-center gap-2 rounded-full font-medium transition-all duration-300 mt-auto",
          highlight
            ? "bg-primary-foreground text-primary hover:bg-primary-foreground/90 hover:scale-105"
            : "bg-primary text-primary-foreground hover:bg-primary/90 hover:scale-105"
        )}
      >
        {buttonText}
        {buttonIcon && React.createElement(buttonIcon, { className: "w-4 h-4" })}
      </button>
    </div>
  );
};
